import { useState, useEffect } from 'react';
import { X, MessageSquare, Minimize2, Maximize2 } from 'lucide-react';
import { MessageThread } from './MessageThread';
import { ConversationList } from './ConversationList';
import { useAuth } from 'utils/AuthContext';
import { useChatStore } from 'utils/chatStore';
import { useProfileStore } from 'utils/useProfileStore';
import { ChatUser, Conversation, isConversationUnreadFor } from 'utils/chatTypes';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

interface ChatSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isMinimized: boolean;
  onToggleMinimize: () => void;
}

export const ChatSidebar = ({ isOpen, onClose, isMinimized, onToggleMinimize }: ChatSidebarProps) => {
  const { user } = useAuth();
  const { profile } = useProfileStore();
  const {
    conversations,
    selectedConversation,
    setSelectedConversation,
    markConversationAsRead
  } = useChatStore();

  const [showConversationList, setShowConversationList] = useState(true);

  // NOTE: We do NOT subscribe to conversations here. The root <App />
  // already maintains a single global subscription in `chatStore`, and
  // we just read from it. Subscribing again would double the Firestore
  // listener count for every chat session.

  // Create current user object for MessageThread
  const currentUser: ChatUser | null = user && profile ? {
    id: user.uid,
    name: user.displayName || ((user as any).firstName ? `${(user as any).firstName} ${(user as any).lastName}` : user.email || 'Unknown User'),
    role: profile.role || 'client',
    photoURL: user.photoURL || undefined
  } : null;

  const handleConversationSelect = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setShowConversationList(false);
    // Mark conversation as read when user opens it
    if (user?.uid) {
      markConversationAsRead(conversation.id, user.uid);
    }
  };

  const handleBackToList = () => {
    setShowConversationList(true);
    setSelectedConversation(null);
  };

  // Reset to conversation list when opening sidebar
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setShowConversationList(true);
      setSelectedConversation(null);
    }
  }, [isOpen, isMinimized, setSelectedConversation]);

  if (!isOpen) return null;
  if (!currentUser) return null;

  return (
    <>
      {/* Sidebar */}
      <div 
        className={`fixed right-0 top-0 h-full shadow-2xl z-50 transition-all duration-300 flex flex-col border-l border-gray-200
          ${isMinimized ? 'w-16 bg-transparent' : 'w-[325px] bg-white dark:bg-gray-900'}
        `}
      >
        {/* Header */}
        <div className={`flex items-center ${isMinimized ? 'justify-center' : 'justify-between'} p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-600 to-teal-500 flex-shrink-0`}>
          {!isMinimized && (
            <div className="flex items-center gap-2 text-white">
              <MessageSquare className="h-5 w-5" />
              <h2 className="font-semibold">Chat</h2>
            </div>
          )}
          <div className={`flex items-center gap-1 ${isMinimized ? 'px-1' : ''}`}>
            <button
              onClick={onToggleMinimize}
              className="p-1.5 hover:bg-white/20 rounded transition-colors text-white"
              title={isMinimized ? "Expand" : "Minimize"}
            >
              {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/20 rounded transition-colors text-white"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {isMinimized ? (
            /* Minimized view - show message icon with count and avatars */
            <div className="flex-1 overflow-y-auto py-2">
              {/* Message icon with unread count at top */}
              {(() => {
                // Calculate total unread count — uses the shared
                // helper so we honour Bug 1 sender guard and Bug 2
                // shared admin_client team-acknowledge state.
                const unreadCount = conversations.filter(conversation =>
                  user?.uid ? isConversationUnreadFor(conversation, user.uid) : false
                ).length;
                
                return (
                  <div className="flex items-center justify-center py-3 mb-2 border-b border-gray-200">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center">
                        <MessageSquare className="h-8 w-8" />
                      </div>
                      {/* Unread count badge */}
                      {unreadCount > 0 && (
                        <div className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-red-500 border-2 border-white rounded-full flex items-center justify-center px-1">
                          <span className="text-white text-xs font-bold">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
              
              {/* Conversation avatars */}
              {conversations.map((conversation) => {
                // Check if conversation has unread messages — same
                // shared helper as the badge count above.
                const isUnread = user?.uid
                  ? isConversationUnreadFor(conversation, user.uid)
                  : false;
                
                const firstLetter = (conversation.name || conversation.otherUserName || 'C').charAt(0).toUpperCase();
                
                return (
                  <button
                    key={conversation.id}
                    onClick={() => {
                      onToggleMinimize(); // Expand first
                      setTimeout(() => handleConversationSelect(conversation), 100);
                    }}
                    className="flex items-center justify-center py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors w-full"
                    title={conversation.name || conversation.otherUserName || 'Conversation'}
                  >
                    {/* Avatar circle */}
                    <div className="relative">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm ${
                        isUnread 
                          ? 'bg-gradient-to-br from-blue-400 to-teal-500' 
                          : 'bg-gray-400 dark:bg-gray-600'
                      }`}>
                        {firstLetter}
                      </div>
                      {/* Unread indicator (red dot) */}
                      {isUnread && (
                        <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full h-5 w-5 border-2 border-white shadow-lg"></span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            /* Expanded view */
            showConversationList ? (
              <div className="flex-1 overflow-y-auto">
                <ConversationList
                  conversations={conversations}
                  selectedConversation={selectedConversation}
                  onSelectConversation={handleConversationSelect}
                  isLoading={false}
                  isMobile={false}
                />
              </div>
            ) : selectedConversation ? (
              <div className="flex-1 overflow-hidden flex flex-col">
                {/* Back button */}
                <div className="p-2 border-b border-gray-200 flex-shrink-0">
                  <Button
                    onClick={handleBackToList}
                    variant="ghost"
                    size="sm"
                    className="p-2"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to conversations
                  </Button>
                </div>
                {/* Message thread */}
                <div className="flex-1 overflow-hidden">
                  <MessageThread 
                    conversation={selectedConversation} 
                    currentUser={currentUser}
                    hideMobileHeader={false}
                    showBackButton={false}
                  />
                </div>
              </div>
            ) : null
          )}
        </div>
      </div>
    </>
  );
};
